<!--
 KB-META
 Purpose: Ai assistant chatbot component
 Role: ui.template
 EntryPoints: onChange→onModelChange, click→showPayloadHistory, click→showRoleInfo, click→clearSessionHistory, click→exportChat
 Inputs: modal; style; draggable; resizable; maximizable
 Outputs: 
 Validation: 
 Dependencies: p-dialog, ng-template, p-dropdown, p-button, p-divider, p-tag
 Contracts: 
 Auth&Privacy: auth:none; hipaa:none; pii:none
 Observability: 
 Router: shard=partner-portal-cura; H2=ai-assistant-chatbot/ai-assistant-chatbot.component; tags=ui,template,a11y
 Owner: ; LastReviewed: 2025-08-25
-->
<p-dialog 
  [(visible)]="visible" 
  [modal]="true" 
  [style]="{ width: '1200px', height: '85vh', minHeight: '700px', maxHeight: '900px' }" 
  header="AI Assistant"
  [draggable]="true" 
  [resizable]="true"
  [maximizable]="true"
  styleClass="ai-assistant-dialog">
  
  <ng-template pTemplate="header">
    <div class="flex items-center justify-between w-full">
      <div class="flex items-center gap-3">
        <i class="pi pi-robot text-2xl text-blue-600"></i>
        <div>
          <h2 class="text-lg font-semibold m-0 text-gray-800">AI Assistant</h2>
          <p class="text-sm text-gray-500 m-0" *ngIf="selectedRole">
            {{ selectedRole.label }}
          </p>
        </div>
      </div>
    </div>
  </ng-template>

  <!-- 2-Column Layout -->
  <div class="ai-assistant-two-column-layout" style="height: calc(85vh - 80px); overflow: hidden;">
    
    <!-- LEFT COLUMN: Settings & Controls -->
    <div class="ai-assistant-left-sidebar">
      <!-- Role Selection -->
      <div class="settings-section">
        <label class="settings-label">
          <i class="pi pi-user-edit mr-2"></i>
          Assistant Role
        </label>
        <p-dropdown 
          [(ngModel)]="selectedRole" 
          [options]="getDisplayRoles()" 
          optionLabel="label"
          placeholder="Select a role"
          (onChange)="onRoleChange()"
          styleClass="w-full role-dropdown-wide"
          [showClear]="false"
          appendTo="body">
          
          <ng-template let-role pTemplate="item">
            <div class="flex items-center gap-2 p-2">
              <div class="role-icon-small" [style.background-color]="role.color + '20'">
                <i [class]="'pi ' + role.icon" [style.color]="role.color"></i>
              </div>
              <div class="flex-1 min-w-0">
                <div class="font-medium text-gray-800 text-sm truncate">{{ role.label }}</div>
                <div class="text-xs text-gray-500 line-clamp-2">{{ role.description }}</div>
              </div>
            </div>
          </ng-template>
          
          <ng-template let-role pTemplate="selectedItem">
            <div class="flex items-center gap-2" *ngIf="role">
              <i [class]="'pi ' + role.icon" [style.color]="role.color"></i>
              <span class="font-medium text-sm truncate">{{ role.label }}</span>
            </div>
          </ng-template>
        </p-dropdown>
      </div>

      <p-divider></p-divider>

      <!-- Model Selection -->
      <div class="settings-section">
        <label class="settings-label">
          <i class="pi pi-cog mr-2"></i>
          AI Model
        </label>
        <p-dropdown 
          [(ngModel)]="selectedModel" 
          [options]="availableModels" 
          optionLabel="label"
          optionValue="value"
          placeholder="Select Model"
          styleClass="w-full"
          (onChange)="onModelChange()">
        </p-dropdown>
        <div class="model-info mt-2">
          <span class="text-xs text-gray-500">Case {{ selectedModelCase }}</span>
        </div>
      </div>

      <p-divider></p-divider>

      <!-- Prompt options (Playground) -->
      <div class="settings-section">
        <label class="settings-label">
          <i class="pi pi-sliders-h mr-2"></i>
          Prompt options (Playground)
        </label>
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div *ngIf="currentCaps.json">
            <label>Text format</label>
            <p-dropdown 
              [(ngModel)]="playgroundSettings.textFormat"
              [options]="[{label:'text',value:'text'},{label:'json',value:'json'}]"
              optionLabel="label" optionValue="value" styleClass="w-full">
            </p-dropdown>
          </div>
          <div *ngIf="currentCaps.reasoning">
            <label>Reasoning effort</label>
            <p-dropdown 
              [(ngModel)]="playgroundSettings.reasoningEffort"
              [options]="[{label:'low',value:'low'},{label:'medium',value:'medium'},{label:'high',value:'high'}]"
              optionLabel="label" optionValue="value" styleClass="w-full">
            </p-dropdown>
          </div>
          <div *ngIf="currentCaps.verbosity">
            <label>Verbosity</label>
            <p-dropdown 
              [(ngModel)]="playgroundSettings.verbosity"
              [options]="[{label:'low',value:'low'},{label:'medium',value:'medium'},{label:'high',value:'high'}]"
              optionLabel="label" optionValue="value" styleClass="w-full">
            </p-dropdown>
          </div>
          <div *ngIf="currentCaps.summary">
            <label>Summary</label>
            <p-dropdown 
              [(ngModel)]="playgroundSettings.summary"
              [options]="[{label:'auto',value:'auto'},{label:'always',value:'always'},{label:'never',value:'never'}]"
              optionLabel="label" optionValue="value" styleClass="w-full">
            </p-dropdown>
          </div>
        </div>
      </div>

      <p-divider></p-divider>

      <!-- Session Info -->
      <div class="settings-section">
        <label class="settings-label">
          <i class="pi pi-clock mr-2"></i>
          Session Info
        </label>
        <div class="session-info-box">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs text-gray-600">Status</span>
            <div class="flex items-center gap-2">
              <div class="status-indicator w-2 h-2 bg-green-400 rounded-full"></div>
              <span class="text-xs text-gray-700 font-medium">Active</span>
            </div>
          </div>
          <div class="text-xs text-gray-600">
            {{ getSessionInfo() }}
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="settings-section">
        <label class="settings-label">
          <i class="pi pi-sliders-h mr-2"></i>
          Actions
        </label>
        
        <div class="action-buttons-grid">
          <p-button 
            label="Role Info" 
            icon="pi pi-info-circle" 
            (click)="showRoleInfo()"
            [disabled]="!selectedRole"
            styleClass="p-button-outlined p-button-sm w-full"
            pTooltip="View role details">
          </p-button>
          
          <p-button 
            label="Clear Chat" 
            icon="pi pi-refresh" 
            (click)="clearSessionHistory()" 
            styleClass="p-button-outlined p-button-sm w-full"
            pTooltip="Clear session history">
          </p-button>
          
          <p-button 
            label="Export" 
            icon="pi pi-download" 
            (click)="exportChat()" 
            styleClass="p-button-outlined p-button-sm w-full"
            pTooltip="Export conversation">
          </p-button>
          
          <p-button 
            label="Debug" 
            icon="pi pi-database" 
            (click)="showPayloadHistory()"
            styleClass="p-button-outlined p-button-sm w-full"
            pTooltip="View payload history">
          </p-button>
        </div>
      </div>

      <p-divider></p-divider>

      <!-- Platform Knowledge (AI Innovator) -->
      <div class="settings-section" *ngIf="selectedRole?.value === 'ai_innovator'">
        <label class="settings-label">
          <i class="pi pi-database mr-2"></i>
          Platform Context
        </label>
        <p-button 
          label="Select Pages" 
          icon="pi pi-plus" 
          (click)="openPageSelectionModal()"
          styleClass="p-button-outlined p-button-sm w-full platform-context-button">
        </p-button>
        <p-tag *ngIf="showPlatformKnowledgeStatus()"
               [value]="getSelectedPagesText()"
               icon="pi-database"
               styleClass="text-xs mt-2 w-full"
               [style]="{ 'background-color': '#059669' + '20', 'color': '#059669' }"
               pTooltip="Platform context loaded ({{ getSelectedPagesTokenEstimate() }} tokens estimated)">
        </p-tag>
      </div>
    </div>

    <!-- RIGHT COLUMN: Chat & Input -->
    <div class="ai-assistant-right-content flex flex-col" style="height: 100%; overflow: hidden;">

      <!-- Chat Messages Container -->
      <div class="chat-container flex-1 overflow-hidden" style="flex: 1 1 auto; min-height: 0;">
        <p-scrollPanel #scrollPanel styleClass="chat-scroll-panel" [style]="{ width: '100%', height: '100%' }">
      <div class="chat-messages p-2">
        
        <!-- Message Loop -->
        <div *ngFor="let message of messages; trackBy: trackMessage" 
             [ngClass]="getMessageClasses(message)"
             class="message-wrapper mb-3">
          
          <!-- AI Message -->
          <div *ngIf="!message.isUser && !message.isTyping" class="ai-message-container">
            <div class="message-header flex items-center justify-between mb-1">
              <div class="flex items-center gap-2">
                <div class="avatar ai-avatar" 
                     [style.background-color]="(selectedRole?.color || '#4f46e5') + '20'"
                     [style.border-color]="(selectedRole?.color || '#4f46e5') + '40'">
                  <i [class]="'pi ' + (selectedRole?.icon || 'pi-robot')" 
                     [style.color]="selectedRole?.color || '#4f46e5'"></i>
                </div>
                <div class="message-info">
                  <span class="font-medium text-sm text-gray-800">
                    {{ selectedRole?.label || 'AI Assistant' }}
                  </span>
                  <span class="text-xs text-gray-500 ml-2">
                    {{ formatTimestamp(message.timestamp) }}
                  </span>
                </div>
              </div>
              <div class="message-actions">
                <p-button 
                  icon="pi pi-trash" 
                  (click)="deleteMessagePair(message.id)"
                  styleClass="p-button-text p-button-sm p-button-danger"
                  pTooltip="Delete this question & answer"
                  tooltipPosition="left">
                </p-button>
              </div>
            </div>
            <div class="message-content ai-content">
              <div [innerHTML]="message.content | marked" class="ai-message-text"></div>
            </div>
          </div>

          <!-- User Message -->
          <div *ngIf="message.isUser" class="user-message-container">
            <div class="message-header flex items-center justify-between mb-1">
              <div class="message-actions">
                <p-button 
                  icon="pi pi-trash" 
                  (click)="deleteMessagePair(message.id)"
                  styleClass="p-button-text p-button-sm p-button-danger"
                  pTooltip="Delete this question & answer"
                  tooltipPosition="right">
                </p-button>
              </div>
              <div class="flex items-center gap-2">
                <div class="message-info text-right">
                  <span class="font-medium text-sm text-gray-800">You</span>
                  <span class="text-xs text-gray-500 ml-2">
                    {{ formatTimestamp(message.timestamp) }}
                  </span>
                </div>
                <div class="avatar user-avatar">
                  <i class="pi pi-user"></i>
                </div>
              </div>
            </div>
            <div class="message-content user-content">
              <div class="user-message-text">{{ message.content }}</div>
            </div>
          </div>

          <!-- Typing Indicator -->
          <div *ngIf="message.isTyping" class="ai-message-container">
            <div class="message-header flex items-center gap-2 mb-1">
              <div class="avatar ai-avatar"
                   [style.background-color]="(selectedRole?.color || '#4f46e5') + '20'"
                   [style.border-color]="(selectedRole?.color || '#4f46e5') + '40'">
                <i [class]="'pi ' + (selectedRole?.icon || 'pi-robot')" 
                   [style.color]="selectedRole?.color || '#4f46e5'"></i>
              </div>
              <div class="message-info">
                <span class="font-medium text-sm text-gray-800">
                  {{ selectedRole?.label || 'AI Assistant' }} is typing...
                </span>
              </div>
            </div>
            <div class="message-content ai-content">
              <div class="typing-indicator">
                <div class="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="messages.length === 0" class="empty-state text-center py-8">
          <i class="pi pi-comments text-4xl text-gray-300 mb-3"></i>
          <h4 class="text-gray-500 mb-2">Start a conversation</h4>
          <p class="text-gray-400 text-sm">
            Select a role above and ask your first question to get started.
          </p>
        </div>

        </div>
      </p-scrollPanel>
    </div>

    <!-- Message Input Area -->
    <div class="message-input-container mt-3 p-3" style="background: #ffffff; border-top: 1px solid #e2e8f0; flex: 0 0 auto;">
      <div class="input-wrapper flex gap-2">
        <textarea 
          pTextarea 
          [(ngModel)]="currentMessage"
          (keydown)="onKeyPress($event)"
          placeholder="Type your message here... (Press Enter to send, Shift+Enter for new line)"
          rows="2"
          [disabled]="isLoading || !selectedRole"
          class="flex-1 message-input"
          [autoResize]="true">
        </textarea>
        
        <div class="send-button-container flex gap-2">
          <!-- Image Generation Button -->
          <p-button 
            icon="pi pi-image" 
            (click)="toggleImageGenPanel()"
            [disabled]="!selectedRole"
            [styleClass]="'p-button-rounded ' + (showImageGenPanel ? 'p-button-success' : '')"
            pTooltip="Generate AI Images"
            tooltipPosition="top">
          </p-button>
          
          <!-- Question Boost Button -->
          <p-button 
            icon="pi pi-lightbulb" 
            (click)="openQuestionBoostModal()"
            [disabled]="!selectedRole"
            styleClass="p-button-rounded question-boost-button"
            pTooltip="Question Boost - Get AI-powered question suggestions"
            tooltipPosition="top">
          </p-button>
          
          <!-- Send Button -->
          <p-button 
            icon="pi pi-send" 
            (click)="sendMessage()"
            [disabled]="!currentMessage.trim() || isLoading || !selectedRole"
            [loading]="isLoading"
            styleClass="p-button-rounded send-button"
            pTooltip="Send message"
            tooltipPosition="top">
          </p-button>
        </div>
      </div>
      
      <!-- Status indicators -->
      <div class="input-status mt-2 flex items-center justify-between">
        <div class="status-left flex items-center gap-2">
          <p-tag *ngIf="showPlatformKnowledgeStatus()"
                 [value]="getSelectedPagesText()"
                 icon="pi-database"
                 styleClass="text-xs platform-knowledge-tag"
                 [style]="{ 'background-color': '#059669' + '20', 'color': '#059669' }"
                 pTooltip="Platform context loaded ({{ getSelectedPagesTokenEstimate() }} tokens estimated)"
                 tooltipPosition="top">
          </p-tag>
        </div>
        
        <div class="status-right text-xs text-gray-500">
          <span *ngIf="!selectedRole">Please select a role to start chatting</span>
          <span *ngIf="selectedRole && !isLoading">Ready to help</span>
          <span *ngIf="isLoading" class="flex items-center gap-1">
            <i class="pi pi-spin pi-spinner"></i>
            Processing...
          </span>
        </div>
      </div>

    <!-- Image Generation Panel -->
    <div *ngIf="showImageGenPanel" class="image-gen-panel mt-3 p-3 border rounded" style="background: white; border-color: #e2e8f0;">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-sm font-semibold flex items-center gap-2">
          <i class="pi pi-image"></i>
          AI Image Generation
        </h4>
        <p-button 
          icon="pi pi-times" 
          (click)="closeImageGenPanel()"
          styleClass="p-button-rounded p-button-text p-button-sm"
          pTooltip="Close">
        </p-button>
      </div>

      <div class="grid grid-cols-1 gap-3">
        <!-- Prompt Input -->
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Image Prompt</label>
          <textarea 
            pTextarea 
            [(ngModel)]="imagePrompt"
            placeholder="Describe the image you want to generate..."
            rows="3"
            class="w-full"
            [autoResize]="true">
          </textarea>
        </div>

        <!-- Model and Aspect Ratio -->
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Provider</label>
            <p-dropdown 
              [(ngModel)]="selectedImageModel.id"
              [options]="availableImageModels"
              optionLabel="name"
              optionValue="id"
              (onChange)="onImageModelChange($event)"
              placeholder="Select Model"
              styleClass="w-full">
            </p-dropdown>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Aspect Ratio</label>
            <p-dropdown 
              [(ngModel)]="selectedAspectRatio"
              [options]="availableAspectRatios"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Ratio"
              styleClass="w-full">
            </p-dropdown>
          </div>
        </div>

        <!-- Generate Button -->
        <div>
          <p-button 
            label="Generate Image" 
            icon="pi pi-sparkles"
            (click)="generateImage()"
            [loading]="isGeneratingImage"
            [disabled]="!imagePrompt.trim() || isGeneratingImage"
            styleClass="w-full p-button-success">
          </p-button>
        </div>

        <!-- Image Preview and Actions -->
        <div *ngIf="generatedImageUrl" class="preview-container">
          <div class="mb-2">
            <img [src]="generatedImageUrl" alt="Generated Image" class="w-full rounded border" style="max-height: 300px; object-fit: contain;">
          </div>
          
          <!-- Action Buttons -->
          <div class="grid grid-cols-2 gap-2">
            <p-button 
              label="Import to Library" 
              icon="pi pi-upload"
              (click)="importToLibrary()"
              styleClass="p-button-sm">
            </p-button>
            <p-button 
              label="Insert in Chat" 
              icon="pi pi-comment"
              (click)="insertImageInChat()"
              styleClass="p-button-sm p-button-outlined">
            </p-button>
          </div>
          
          <div class="grid grid-cols-2 gap-2 mt-2">
            <p-button 
              label="Regenerate" 
              icon="pi pi-refresh"
              (click)="regenerateImage()"
              [loading]="isGeneratingImage"
              styleClass="p-button-sm p-button-outlined">
            </p-button>
            <p-button 
              label="Download" 
              icon="pi pi-download"
              (click)="downloadGeneratedImage()"
              styleClass="p-button-sm p-button-outlined">
            </p-button>
          </div>
        </div>
      </div>
      </div>
    </div> <!-- End message-input-container -->

    </div> <!-- End ai-assistant-right-content -->
  </div> <!-- End ai-assistant-two-column-layout -->

</p-dialog>

<!-- Role Information Dialog -->
<p-dialog 
  header="Role Information" 
  [(visible)]="isRoleInfoVisible" 
  [modal]="true" 
  [style]="{ width: '50vw' }"
  [draggable]="false"
  [resizable]="false"
  styleClass="ai-role-info-dialog">
  
  <div *ngIf="selectedRole">
    <p class="mb-2">
      <strong>Role:</strong> {{ selectedRole.label }}
    </p>
    <p class="mb-4">
      <strong>JSON Path:</strong> 
      <code class="bg-gray-100 text-gray-800 p-1 rounded text-sm">{{ roleInfoPath }}</code>
    </p>

    <p-divider></p-divider>
    
    <h4 class="font-semibold mb-2">System Role Content:</h4>
    <pre><code class="language-json">{{ roleInfoContent }}</code></pre>
  </div>

  <ng-template pTemplate="footer">
      <p-button 
        icon="pi pi-times" 
        (click)="isRoleInfoVisible = false" 
        label="Close" 
        styleClass="p-button-text">
      </p-button>
  </ng-template>
</p-dialog>

<p-toast></p-toast>

<!-- Payload History Dialog -->
<p-dialog 
  header="Payload History - Debug AI Calls" 
  [(visible)]="isPayloadHistoryVisible" 
  [modal]="true" 
  [style]="{ width: '90vw', height: '80vh' }"
  [draggable]="true"
  [resizable]="true"
  [maximizable]="true"
  styleClass="payload-history-dialog">
  
  <ng-template pTemplate="header">
    <div class="flex items-center justify-between w-full">
      <div class="flex items-center gap-3">
        <i class="pi pi-database text-2xl text-blue-600"></i>
        <div>
          <h2 class="text-lg font-semibold m-0 text-gray-800">API Payload History</h2>
          <p class="text-sm text-gray-500 m-0">
            Sessions: {{ payloadSessions.length }} | Total Payloads: {{ payloadHistory.length }}
          </p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <p-button 
          icon="pi pi-download" 
          (click)="exportPayloadHistory()" 
          styleClass="p-button-text p-button-sm"
          pTooltip="Export payload history"
          tooltipPosition="bottom">
        </p-button>
        <p-button 
          icon="pi pi-trash" 
          (click)="clearPayloadHistory()" 
          styleClass="p-button-text p-button-sm p-button-danger"
          pTooltip="Clear all history"
          tooltipPosition="bottom">
        </p-button>
      </div>
    </div>
  </ng-template>

  <div class="payload-history-container" *ngIf="payloadHistory.length > 0; else noPayloads">
    
    <!-- Recent Payloads Overview -->
    <div class="mb-4">
      <h3 class="text-lg font-semibold mb-3 text-gray-800">Recent API Calls</h3>
      
      <p-table [value]="payloadHistory" [scrollable]="true" scrollHeight="400px" [paginator]="true" [rows]="10">
        <ng-template pTemplate="header">
          <tr>
            <th>Session</th>
            <th>Time</th>
            <th>Role</th>
            <th>Status</th>
            <th>Model</th>
            <th>User Input</th>
            <th>Response Time</th>
            <th>API Endpoint</th>
            <th>Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-payload>
          <tr>
            <td class="text-xs font-mono">{{ payload.sessionId.slice(-8) }}</td>
            <td>{{ formatTimestamp(payload.timestamp) }}</td>
            <td>
              <span class="role-badge px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                {{ payload.request.roleLabel }}
              </span>
            </td>
            <td>
              <span class="status-badge px-2 py-1 rounded text-xs font-medium"
                    [style.background-color]="getPayloadStatusColor(payload.response.status) + '20'"
                    [style.color]="getPayloadStatusColor(payload.response.status)">
                {{ payload.response.status | titlecase }}
              </span>
            </td>
            <td class="text-sm font-mono">{{ payload.request.model }}</td>
            <td class="text-sm">
              <div class="truncate max-w-xs" [title]="payload.request.userInput">
                {{ payload.request.userInput }}
              </div>
            </td>
            <td class="text-sm">{{ payload.response.responseTime }}ms</td>
            <td class="text-xs font-mono">{{ payload.request.apiEndpoint.split('/').pop() }}</td>
            <td>
              <div class="flex items-center gap-1">
                <p-button 
                  icon="pi pi-eye" 
                  styleClass="p-button-text p-button-sm"
                  pTooltip="View details"
                  (click)="showPayloadDetails(payload)">
                </p-button>
                <p-button 
                  icon="pi pi-copy" 
                  styleClass="p-button-text p-button-sm"
                  pTooltip="Copy payload"
                  (click)="copyPayload(payload)">
                </p-button>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>

  <ng-template #noPayloads>
    <div class="text-center py-8">
      <i class="pi pi-inbox text-4xl text-gray-300 mb-3"></i>
      <h4 class="text-gray-500 mb-2">No API History</h4>
      <p class="text-gray-400 text-sm">
        Start a conversation with the Genus Pilot Assistant to see API calls here.
      </p>
    </div>
  </ng-template>

  <ng-template pTemplate="footer">
    <div class="flex items-center justify-between w-full">
      <div class="text-sm text-gray-500">
        Storing last 50 payloads locally for debugging
      </div>
      <p-button 
        icon="pi pi-times" 
        (click)="isPayloadHistoryVisible = false" 
        label="Close" 
        styleClass="p-button-text">
      </p-button>
    </div>
  </ng-template>
</p-dialog>

<!-- Payload Details Dialog -->
<p-dialog 
  header="API Call Details" 
  [(visible)]="isPayloadDetailsVisible" 
  [modal]="true" 
  [style]="{ width: '80vw', height: '70vh' }"
  [draggable]="true"
  [resizable]="true"
  [maximizable]="true"
  styleClass="payload-details-dialog">
  
  <div *ngIf="selectedPayloadDetails" class="payload-details-content">
    
    <!-- Quick Overview -->
    <div class="mb-4 p-4 bg-gray-50 rounded-lg">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <div class="text-sm text-gray-500">Status</div>
          <span class="status-badge px-2 py-1 rounded text-xs font-medium"
                [style.background-color]="getPayloadStatusColor(selectedPayloadDetails.response.status) + '20'"
                [style.color]="getPayloadStatusColor(selectedPayloadDetails.response.status)">
            {{ selectedPayloadDetails.response.status | titlecase }}
          </span>
        </div>
        <div>
          <div class="text-sm text-gray-500">Response Time</div>
          <div class="font-medium">{{ selectedPayloadDetails.response.responseTime }}ms</div>
        </div>
        <div>
          <div class="text-sm text-gray-500">Model</div>
          <div class="font-medium">{{ selectedPayloadDetails.request.model }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-500">Timestamp</div>
          <div class="font-medium">{{ formatTimestamp(selectedPayloadDetails.timestamp) }}</div>
        </div>
      </div>
    </div>

    <!-- Tabbed Content -->
    <p-tabView>
      <p-tabPanel header="Request Details">
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">User Input</label>
            <textarea readonly class="w-full p-3 border rounded-lg bg-gray-50 text-sm" rows="3">{{ selectedPayloadDetails.request.userInput }}</textarea>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Endpoint</label>
            <code class="block p-2 bg-gray-100 rounded text-sm">{{ selectedPayloadDetails.request.apiEndpoint }}</code>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Parameters</label>
            <pre class="p-3 bg-gray-100 rounded-lg text-sm overflow-auto max-h-40">{{ selectedPayloadDetails.request.parameters | json }}</pre>
          </div>
        </div>
      </p-tabPanel>

      <p-tabPanel header="Response">
        <div class="space-y-4">
          <div *ngIf="selectedPayloadDetails.response.status === 'error' && selectedPayloadDetails.response.errorDetails">
            <label class="block text-sm font-medium text-red-700 mb-1">Error Details</label>
            <pre class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{{ selectedPayloadDetails.response.errorDetails }}</pre>
          </div>

          <div *ngIf="selectedPayloadDetails.response.status === 'success'">
            <label class="block text-sm font-medium text-gray-700 mb-1">Response Content</label>
            <div class="p-3 border rounded-lg bg-gray-50 max-h-60 overflow-auto">
              <div [innerHTML]="selectedPayloadDetails.response.content | marked" class="text-sm"></div>
            </div>
          </div>
        </div>
      </p-tabPanel>

      <p-tabPanel header="Raw JSON">
        <div class="relative">
          <button 
            class="absolute top-2 right-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
            (click)="copyPayload(selectedPayloadDetails!)">
            <i class="pi pi-copy mr-1"></i>Copy JSON
          </button>
          <pre class="p-4 bg-gray-100 rounded-lg text-xs overflow-auto max-h-96 mt-8">{{ selectedPayloadDetails | json }}</pre>
        </div>
      </p-tabPanel>
    </p-tabView>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex items-center justify-between w-full">
      <div class="text-sm text-gray-500">
        Payload ID: {{ selectedPayloadDetails?.id }}
      </div>
      <div class="flex gap-2">
        <p-button 
          icon="pi pi-copy" 
          (click)="copyPayload(selectedPayloadDetails!)" 
          label="Copy JSON" 
          styleClass="p-button-outlined p-button-sm">
        </p-button>
        <p-button 
          icon="pi pi-times" 
          (click)="isPayloadDetailsVisible = false" 
          label="Close" 
          styleClass="p-button-text">
        </p-button>
      </div>
    </div>
  </ng-template>
</p-dialog>

<!-- Question Boost Modal -->
<p-dialog 
  [(visible)]="showQuestionBoostModal" 
  [modal]="true" 
  [style]="{ width: '700px', minHeight: '500px' }" 
  header="Question Boost - AI-Powered Suggestions"
  [draggable]="true" 
  [resizable]="true"
  styleClass="question-boost-dialog">
  
  <div class="question-boost-modal-content">
    <!-- Header Info -->
    <div class="boost-header mb-4 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg">
      <div class="flex items-center gap-3">
        <div class="boost-icon-container">
          <i class="pi pi-lightbulb text-yellow-600 text-2xl"></i>
        </div>
        <div class="flex-1">
          <h3 class="text-lg font-semibold text-gray-800 m-0">Smart Question Generation</h3>
          <p class="text-sm text-gray-600 m-0 mt-1">
            AI-powered suggestions tailored for <span class="font-medium text-blue-600">{{ selectedRole?.label }}</span>
          </p>
        </div>
        <div class="boost-stats text-center">
          <div class="text-xs text-gray-500">History</div>
          <div class="font-bold text-lg text-gray-800">{{ questionHistory.length }}</div>
        </div>
      </div>
    </div>

    <!-- Tab Controls -->
    <div class="boost-tabs mb-4">
      <div class="flex border-b border-gray-200">
        <button 
          class="tab-button px-4 py-2 text-sm font-medium transition-colors"
          [class.active]="activeBoostTab === 'generate'"
          (click)="setActiveBoostTab('generate')">
          <i class="pi pi-sparkles mr-2"></i>
          Generate New
        </button>
        <button 
          class="tab-button px-4 py-2 text-sm font-medium transition-colors"
          [class.active]="activeBoostTab === 'history'"
          (click)="setActiveBoostTab('history')">
          <i class="pi pi-history mr-2"></i>
          History ({{ questionHistory.length }})
        </button>
      </div>
    </div>

    <!-- Generate Tab -->
    <div *ngIf="activeBoostTab === 'generate'" class="generate-tab">
      
      <!-- Generation Controls -->
      <div class="generation-controls mb-4 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <p-button 
            icon="pi pi-refresh" 
            label="Generate Questions"
            (click)="generateQuestionsModal()"
            [loading]="isGeneratingQuestions"
            styleClass="p-button-sm"
            [disabled]="!selectedRole">
          </p-button>
          <span *ngIf="currentQuestionSuggestions.length > 0" class="text-xs text-gray-500">
            {{ currentQuestionSuggestions.length }} suggestions generated
          </span>
        </div>
        <div class="text-xs text-gray-500">
          <i class="pi pi-info-circle mr-1"></i>
          Questions adapt to your conversation context
        </div>
      </div>

      <!-- Generation Animation -->
      <div *ngIf="isGeneratingQuestions" class="generation-animation mb-4 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg text-center">
        <div class="generation-spinner mb-3">
          <i class="pi pi-spin pi-cog text-blue-500 text-3xl"></i>
        </div>
        <h4 class="text-lg font-medium text-gray-800 mb-2">Generating Smart Questions...</h4>
        <p class="text-sm text-gray-600 mb-3">
          Analyzing your role, conversation context, and best practices
        </p>
        <div class="generation-steps">
          <div class="step" [class.active]="generationStep >= 1">
            <i class="pi pi-check-circle"></i> Analyzing role context
          </div>
          <div class="step" [class.active]="generationStep >= 2">
            <i class="pi pi-check-circle"></i> Processing conversation history
          </div>
          <div class="step" [class.active]="generationStep >= 3">
            <i class="pi pi-check-circle"></i> Generating personalized questions
          </div>
        </div>
      </div>

      <!-- Generated Questions -->
      <div *ngIf="!isGeneratingQuestions && currentQuestionSuggestions.length > 0" class="generated-questions">
        <div class="questions-header mb-3">
          <h4 class="text-base font-medium text-gray-800 m-0">Generated Questions</h4>
          <p class="text-xs text-gray-500 mt-1">Click any question to use it in your conversation</p>
        </div>
        
        <div class="questions-list space-y-3">
          <div *ngFor="let suggestion of currentQuestionSuggestions; let i = index" 
               class="question-card p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
               (click)="selectSuggestionFromModal(suggestion)">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-2">
                  <span class="question-number">{{ i + 1 }}</span>
                  <span class="category-badge">{{ suggestion.category }}</span>
                </div>
                <p class="question-text text-sm font-medium text-gray-800 mb-1">
                  {{ suggestion.question }}
                </p>
                <p class="question-context text-xs text-gray-500">
                  {{ suggestion.context }}
                </p>
              </div>
              <div class="question-actions ml-3">
                <i class="pi pi-arrow-right text-gray-400 hover:text-blue-500 transition-colors"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div *ngIf="!isGeneratingQuestions && currentQuestionSuggestions.length === 0" class="empty-state text-center py-8">
        <div class="empty-icon mb-3">
          <i class="pi pi-lightbulb text-gray-300 text-4xl"></i>
        </div>
        <h4 class="text-base font-medium text-gray-600 mb-2">Ready to Generate Questions</h4>
        <p class="text-sm text-gray-500 mb-4">
          Click "Generate Questions" to get AI-powered suggestions tailored to your {{ selectedRole?.label }} role
        </p>
      </div>
    </div>

    <!-- History Tab -->
    <div *ngIf="activeBoostTab === 'history'" class="history-tab">
      <div class="history-header mb-3">
        <h4 class="text-base font-medium text-gray-800 m-0">Question History</h4>
        <p class="text-xs text-gray-500 mt-1">Previously generated questions you can reuse</p>
      </div>

      <div *ngIf="questionHistory.length > 0" class="history-list space-y-2 max-h-80 overflow-y-auto">
        <div *ngFor="let historyItem of questionHistory; let i = index" 
             class="history-card p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
             (click)="selectSuggestionFromModal(historyItem)">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <span class="history-date text-xs text-gray-400">
                  {{ formatDate(historyItem.timestamp) }}
                </span>
                <span class="category-badge-small">{{ historyItem.category }}</span>
              </div>
              <p class="history-question text-sm text-gray-800">
                {{ historyItem.question }}
              </p>
            </div>
            <div class="history-actions ml-3">
              <i class="pi pi-arrow-right text-gray-400 hover:text-blue-500 transition-colors"></i>
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="questionHistory.length === 0" class="empty-history text-center py-8">
        <div class="empty-icon mb-3">
          <i class="pi pi-history text-gray-300 text-4xl"></i>
        </div>
        <h4 class="text-base font-medium text-gray-600 mb-2">No Question History</h4>
        <p class="text-sm text-gray-500">
          Generated questions will appear here for easy reuse
        </p>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer mt-6 pt-4 border-t border-gray-200">
      <div class="flex justify-between items-center">
        <div class="footer-info text-xs text-gray-500">
          <i class="pi pi-info-circle mr-1"></i>
          Questions are personalized based on your role and conversation context
        </div>
        <div class="footer-actions">
          <p-button 
            label="Close" 
            (click)="closeQuestionBoostModal()"
            styleClass="p-button-text p-button-sm">
          </p-button>
        </div>
      </div>
    </div>
  </div>
</p-dialog>

<!-- Platform Knowledge Selection Modal -->
<p-dialog 
  [(visible)]="showPageSelectionModal" 
  [modal]="true" 
  [style]="{ width: '900px', height: '80vh' }" 
  header="Platform Knowledge Selection - AI Innovator Context"
  [draggable]="true" 
  [resizable]="true"
  styleClass="page-selection-dialog">
  
  <div class="platform-knowledge-modal-content">
    <!-- Header Info -->
    <div class="knowledge-header mb-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg">
      <div class="flex items-center gap-4">
        <div class="knowledge-icon-container">
          <i class="pi pi-database text-indigo-600 text-3xl"></i>
        </div>
        <div class="flex-1">
          <h3 class="text-lg font-semibold text-gray-800 m-0">Select Platform Areas for Innovation Context</h3>
          <p class="text-sm text-gray-600 m-0 mt-1">
            Choose up to {{ maxSelectedPages }} platform pages to provide deep business logic and innovation context to the AI Innovator
          </p>
        </div>
        <div class="knowledge-stats grid grid-cols-2 gap-3 text-center">
          <div>
            <div class="text-xs text-gray-500">Available</div>
            <div class="font-bold text-lg text-gray-800">{{ knowledgeStats.totalPages }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-500">Selected</div>
            <div class="font-bold text-lg text-indigo-600">{{ selectedPages.length }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Search and Filters -->
    <div class="search-filters mb-4">
      <div class="flex items-center gap-3">
        <div class="flex-1">
          <span class="p-input-icon-left w-full">
            <i class="pi pi-search"></i>
            <input 
              type="text" 
              pInputText 
              [(ngModel)]="searchTerm" 
              (input)="searchPages()"
              placeholder="Search platform pages..." 
              class="w-full">
          </span>
        </div>
        <p-button 
          icon="pi pi-filter-slash" 
          label="Clear Selection"
          (click)="clearPageSelection()"
          [disabled]="selectedPages.length === 0"
          styleClass="p-button-outlined p-button-sm">
        </p-button>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoadingPages" class="loading-state text-center py-12">
      <i class="pi pi-spin pi-spinner text-indigo-500 text-4xl mb-4"></i>
      <h4 class="text-lg font-medium text-gray-700 mb-2">Loading Platform Knowledge</h4>
      <p class="text-sm text-gray-500">Fetching available pages and business logic...</p>
    </div>

    <!-- Content Area -->
    <div *ngIf="!isLoadingPages" class="content-area">
      <!-- Selection Summary -->
      <div *ngIf="selectedPages.length > 0" class="selection-summary mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <i class="pi pi-check-circle text-green-600"></i>
            <span class="text-sm font-medium text-green-800">
              {{ selectedPages.length }} platform area{{ selectedPages.length > 1 ? 's' : '' }} selected
            </span>
          </div>
          <div class="text-xs text-green-600">
            ~{{ getSelectedPagesTokenEstimate() }} tokens estimated
          </div>
        </div>
        <div class="selected-pages-list mt-2">
          <div class="flex flex-wrap gap-2">
            <p-tag *ngFor="let page of selectedPages" 
                   [value]="page.name"
                   [style]="{ 'background-color': '#059669' + '20', 'color': '#059669' }"
                   icon="pi-check"
                   styleClass="text-xs">
            </p-tag>
          </div>
        </div>
      </div>

      <!-- Available Pages -->
      <div class="available-pages">
        <h4 class="text-base font-medium text-gray-800 mb-3">Available Platform Pages</h4>
        
                 <!-- Category Groups -->
         <div *ngFor="let category of getObjectKeys(pagesByCategory)" class="category-group mb-4">
          <div class="category-header mb-2">
            <div class="flex items-center gap-2">
              <i [class]="'pi ' + getCategoryIcon(category)" class="text-gray-600"></i>
              <h5 class="text-sm font-semibold text-gray-700 m-0">{{ category }}</h5>
              <span class="text-xs text-gray-500">({{ pagesByCategory[category].length }} pages)</span>
            </div>
          </div>
          
          <div class="category-pages grid grid-cols-1 gap-2">
            <div *ngFor="let page of pagesByCategory[category]" 
                 class="page-card p-3 border rounded-lg cursor-pointer transition-all"
                 [class.selected]="isPageSelected(page)"
                 [class.border-indigo-300]="isPageSelected(page)"
                 [class.bg-indigo-50]="isPageSelected(page)"
                 [class.border-gray-200]="!isPageSelected(page)"
                 [class.bg-white]="!isPageSelected(page)"
                 [class.hover:border-indigo-200]="!isPageSelected(page)"
                 [class.hover:bg-gray-50]="!isPageSelected(page)"
                 (click)="togglePageSelection(page)">
              
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-2">
                    <div class="page-checkbox">
                      <i [class]="isPageSelected(page) ? 'pi pi-check-square' : 'pi pi-square-o'"
                         [class.text-indigo-600]="isPageSelected(page)"
                         [class.text-gray-400]="!isPageSelected(page)"></i>
                    </div>
                    <h6 class="text-sm font-medium text-gray-800 m-0">{{ page.name }}</h6>
                    <span class="complexity-indicator flex items-center gap-1 text-xs">
                      <i [class]="'pi ' + getComplexityIcon(page.complexity)" 
                         [style.color]="getComplexityColor(page.complexity)"></i>
                      <span [style.color]="getComplexityColor(page.complexity)">{{ page.complexity }}</span>
                    </span>
                  </div>
                  <p class="text-xs text-gray-600 mb-2">{{ page.description }}</p>
                  <div class="page-meta flex items-center gap-3 text-xs text-gray-500">
                    <span>Route: {{ page.route }}</span>
                    <span *ngIf="page.estimatedTokens">~{{ page.estimatedTokens }} tokens</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty Search Results -->
        <div *ngIf="filteredPages.length === 0 && searchTerm" class="empty-search text-center py-8">
          <i class="pi pi-search text-gray-300 text-4xl mb-3"></i>
          <h4 class="text-base font-medium text-gray-600 mb-2">No pages found</h4>
          <p class="text-sm text-gray-500">
            Try adjusting your search terms or browse by category
          </p>
        </div>
      </div>

      <!-- Recommendations -->
      <div *ngIf="recommendedPages.length > 0" class="recommendations mt-6">
        <h4 class="text-base font-medium text-gray-800 mb-3">
          <i class="pi pi-thumbs-up mr-2 text-indigo-600"></i>
          Recommended Pages
        </h4>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div *ngFor="let page of recommendedPages.slice(0, 4)" 
               class="recommendation-card p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
               (click)="togglePageSelection(page)">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h6 class="text-sm font-medium text-gray-800 m-0">{{ page.name }}</h6>
                <p class="text-xs text-gray-600 mt-1">{{ page.category }}</p>
              </div>
              <div class="rec-action">
                <i [class]="isPageSelected(page) ? 'pi pi-check-circle text-green-600' : 'pi pi-plus-circle text-blue-600'"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer mt-6 pt-4 border-t border-gray-200">
      <div class="flex justify-between items-center">
        <div class="footer-info text-xs text-gray-500">
          <i class="pi pi-info-circle mr-1"></i>
          Selected pages will be included as business context for innovation discussions
        </div>
        <div class="footer-actions flex gap-2">
          <p-button 
            label="Cancel" 
            (click)="closePageSelectionModal()"
            styleClass="p-button-text p-button-sm">
          </p-button>
          <p-button 
            label="Apply Context" 
            icon="pi pi-check"
            (click)="applyPlatformContext()"
            [disabled]="selectedPages.length === 0"
            [loading]="isContextUpdating"
            styleClass="p-button-sm">
          </p-button>
        </div>
      </div>
    </div>
  </div>
</p-dialog> 